/* eslint-disable camelcase */
import { useEffect } from 'react';
import nookies from 'nookies';
import Loader from 'components/LandingPage/Loader.jsx';
import PageMeta from 'components/PageMeta';
import logger from 'config/logger';
import getContentData from 'data/contentstack/get-content-data';
import { getSegmentedLandingPageContent } from 'data/contentstack/get-segmented-landing-page-content';
import universalHydration from 'data/universal-hydration';
import useGetSegmentedLandingPage from 'hooks/useGetSegmentedLandingPage';
import sspMiddleware from 'middleware/gssp';
import setCaching from 'utils/cache-control';
import getCanonicalUrl from 'utils/get-canonical-url';
import getCorrelationId from 'utils/get-correlation-id';
import { sendPageView, setMetricsData } from 'utils/metrics';

export default function GenericPage({
  canonical,
  groupedDiscountsContent,
  location,
  menu,
  productLabelsContent,
  pageData: propPageData,
}) {
  const { pageData } = useGetSegmentedLandingPage({
    data: propPageData,
    location,
  });

  const { component_list: components = [], seo = {} } = pageData;
  const { meta_title: title, meta_description: description, keywords } = seo;

  useEffect(() => {
    setMetricsData({
      location,
      menu,
      pageType: 'landing_page',
      title,
    });
    sendPageView(title);
  }, [title, location, menu]);

  return (
    <>
      <PageMeta
        canonical={canonical}
        description={description}
        keywords={keywords}
        title={title}
      />
      {components.map((component) => {
        const Component = Loader(component._content_type_uid);

        if (!Component) return null;

        return (
          <Component
            key={component.uid}
            groupedDiscountsContent={groupedDiscountsContent}
            productLabelsContent={productLabelsContent}
            {...component}
          />
        );
      })}
    </>
  );
}

export const getServerSideProps = sspMiddleware((store) => async (ctx) => {
  setCaching('medium', ctx.res);
  const { log = logger } = ctx.req;
  const correlationId = getCorrelationId({ request: ctx.req });

  const { store: location = '', menu = 'recreational' } = nookies.get(ctx);

  const { slug } = ctx.params;
  if (slug[0] === 'pages') {
    try {
      const url = slug.slice(1).join('/');
      return {
        redirect: {
          destination: `/${url}`,
          permanent: true,
        },
      };
    } catch (err) {
      log.error(err, 'Error redirecting to generic landing page url');
    }
  }

  const path = `/${slug}`;

  const [universal, pageData, groupedDiscounts, productLabels] =
    await Promise.allSettled([
      universalHydration({ ctx, store, correlationId, log }),
      getSegmentedLandingPageContent({
        cmsUrlParam: path,
        correlationId,
        location,
        log,
      }),
      getContentData({
        id: 'grouped_product_discounts',
        correlationId,
        log,
        params: {
          group_id: 'tiered-flower',
        },
      }),
      getContentData({ id: 'product_labels', correlationId, log }),
    ]);

  const props = {
    canonical: getCanonicalUrl(path),
    location,
    menu,
  };

  if (pageData.status !== 'fulfilled' || !pageData.value) {
    return {
      notFound: true,
    };
  }

  if (universal.status === 'fulfilled') {
    props.footer = universal.value.footer;
    props.header = universal.value.header;
  }

  if (pageData.status === 'fulfilled') {
    props.pageData = pageData.value;
  }

  if (groupedDiscounts.status === 'fulfilled') {
    props.groupedDiscountsContent =
      groupedDiscounts.value?.data?.[0]?.discounts ?? [];
  }

  if (productLabels.status === 'fulfilled') {
    props.productLabelsContent = productLabels.value?.data?.[0] ?? {};
  }

  if (!props.pageData?.component_list) {
    return {
      notFound: true,
    };
  }

  props.metrics = {
    title: props.seo?.meta_title ?? 'Generic Lander Page',
    pageType: 'landing_page',
    location,
    menu,
  };

  return {
    props,
  };
});
